import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
} from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import { useDiscount } from "./DiscountContext";
import * as api from "../api/index";
import { StockContext } from "./StockContext";
import { useOrderSession } from "./OrderSessionContext";
import { useCompany } from "./CompanyContext";
import { applyCurrencyRounding } from "../utils/currency";
import useLiveData from "../realtime/useLiveData";

const CartContext = createContext();
export function useCart() {
  return useContext(CartContext);
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [isCartSubmitting, setIsCartSubmitting] = useState(false);
  const [QtydialogOpen, setQtyDialogOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [receivedAmount, setReceivedAmount] = useState("");
  const [vat, setVat] = useState();
  const { company } = useCompany();
  const [minQtyAlerted, setMinQtyAlerted] = useState({});
  const [lowStockDialog, setLowStockDialog] = useState({
    open: false,
    itemName: "",
    remainingStock: 0,
    minQty: 0,
    unitLabel: "",
  });
  // Separate from lowStockDialog above: this is the hard "cannot sell"
  // message (stock would hit zero/negative), kept distinct from the
  // informational minimum-quantity warning per product requirements.
  const [outOfStockDialog, setOutOfStockDialog] = useState({
    open: false,
    itemName: "",
    availableStock: 0,
    unitLabel: "",
  });
  // Third, separate case: Allow Negative Stock = ON and a toggleStock item's
  // resulting stock actually went negative. Distinct from both dialogs above -
  // the sale is NOT blocked here, this is just a notice + the isNegativeStock
  // flag stored on the cart item (see evaluateStockAlerts) that CartTable.js
  // uses to badge/highlight that one row.
  const [negativeStockDialog, setNegativeStockDialog] = useState({
    open: false,
    itemName: "",
    remainingStock: 0,
    unitLabel: "",
  });
  // Debounces negativeStockDialog the same way minQtyAlerted debounces
  // lowStockDialog - fires once per item per dip into negative territory,
  // not on every subsequent increment while it stays negative.
  const [negativeStockAlerted, setNegativeStockAlerted] = useState({});
  // "Minimum Sale Quantity" Special Feature (see GlobalSettingsModel.js) -
  // distinct from lowStockDialog above (that `minQty` is stockQty.minQty,
  // an unrelated low-stock ALERT threshold; this one is units.sale.minQty,
  // the minimum quantity a single sale of this item must be).
  const [minSaleQtyDialog, setMinSaleQtyDialog] = useState({
    open: false,
    itemName: "",
    minQty: 0,
    unitLabel: "",
  });

  const { discountFlag, clearDiscount, discountVal, discountType, discountRate } = useDiscount();
  const { getCurrentStockQty, getConvertedQuantity } = useContext(StockContext);
  const orderSession = useOrderSession();
  const authData = useSelector((state) => state.auth?.authData);
  // Settings > Stock Settings > "Allow Negative Stock" (Company.allowNegativeStock,
  // default false). OFF (default) blocks add/increase/manual-qty actions that
  // would take a toggleStock item's stock to zero or below.
  const allowNegativeStock = company?.allowNegativeStock === true;

  // When editing an existing invoice, its original SALES rows are still in
  // the stock ledger. Available qty must add those back; otherwise the cart
  // treats already-sold lines as consuming stock a second time, and saving
  // can restore-without-rededucting. Values are the original converted sale
  // qty (negative) keyed by item id.
  const [invoiceEditStockCredit, setInvoiceEditStockCredit] = useState({});

  const creditForItem = (itemId, creditMap = invoiceEditStockCredit) =>
    Number(creditMap[String(itemId)] || 0);

  // currentStock already includes this invoice's original (negative) sale.
  // Subtracting that sale adds it back, then the proposed cart sale is applied.
  const remainingAfterProposedSale = (
    currentStock,
    itemId,
    proposedConvertedQty,
    creditMap = invoiceEditStockCredit
  ) =>
    Number(currentStock || 0) -
    creditForItem(itemId, creditMap) +
    Number(proposedConvertedQty || 0);

  const availableIncludingThisInvoice = (
    currentStock,
    itemId,
    creditMap = invoiceEditStockCredit
  ) => Number(currentStock || 0) - creditForItem(itemId, creditMap);

  const buildInvoiceStockCredit = (items = []) => {
    const credit = {};
    items.forEach((line) => {
      if (!line?._id || line.isStaffReq || !line.toggleStock) return;
      const converted = getConvertedQuantity(
        line,
        "sale",
        line.units?.stock?.unit?._id
      );
      const id = String(line._id);
      credit[id] = (credit[id] || 0) + (Number(converted) || 0);
    });
    return credit;
  };

  const hydrateInvoiceCart = async (mappedItems = []) => {
    const credit = buildInvoiceStockCredit(mappedItems);
    setInvoiceEditStockCredit(credit);
    const withStock = await Promise.all(
      mappedItems.map(async (item) => {
        if (item.isStaffReq || !item.toggleStock) return item;
        try {
          const data = await getCurrentStockQty(item._id);
          const proposed = getConvertedQuantity(
            item,
            "sale",
            item.units?.stock?.unit?._id
          );
          return {
            ...item,
            stockAfterSale: remainingAfterProposedSale(
              data.currentStock,
              item._id,
              proposed,
              credit
            ),
          };
        } catch (error) {
          return item;
        }
      })
    );
    setCartItems(withStock);
  };

  const clearInvoiceStockCredit = () => setInvoiceEditStockCredit({});

  // Live: another till sold / received / adjusted stock of an item that's in
  // this cart - refresh each affected line's stock-after-this-sale and its
  // low / negative flags (the red row). Quantities and prices are never
  // touched, and no alert dialog pops up mid-sale for a background change.
  // Uses the API directly: getCurrentStockQty turns a failed request into
  // "0 in stock", which would wrongly flag the line as negative.
  useLiveData(["stock"], async (changes) => {
    const tracked = cartItems.filter((line) => line?._id && line.toggleStock && !line.isStaffReq);
    if (!tracked.length) return;
    const moved = !changes || changes.stock?.all ? null : new Set(changes.stock?.ids || []);
    const ids = [...new Set(tracked.map((line) => String(line._id)))].filter(
      (id) => !moved || moved.has(id)
    );
    if (!ids.length) return;

    const stockById = {};
    await Promise.all(
      ids.map(async (id) => {
        try {
          const { data } = await api.getCurrentStockQuantity(id);
          const qty = parseFloat(data?.currentStock?.$numberDecimal ?? data?.currentStock);
          if (Number.isFinite(qty)) stockById[id] = qty;
        } catch (error) {
          // Keep the line's last known figure.
        }
      })
    );
    if (!Object.keys(stockById).length) return;

    setCartItems((prev) =>
      prev.map((line) => {
        const id = String(line?._id || "");
        if (!(id in stockById) || !line.toggleStock || line.isStaffReq) return line;
        const proposed = getConvertedQuantity(line, "sale", line.units?.stock?.unit?._id);
        const stockAfterSale = remainingAfterProposedSale(stockById[id], id, proposed);
        const isNegativeStock = stockAfterSale < 0;
        const isLowStock =
          !isNegativeStock && stockAfterSale <= parseFloat(line?.stockQty?.minQty || 0);
        return { ...line, stockAfterSale, isNegativeStock, isLowStock };
      })
    );
  });

  // Clear cart when user logs out or switches users
  useEffect(() => {
    if (!authData) {
      setCartItems([]);
      setReceivedAmount("");
      setMinQtyAlerted({});
      setNegativeStockAlerted({});
      setInvoiceEditStockCredit({});
      clearDiscount();
    }
  }, [authData, clearDiscount]);

  const parsePriceValue = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const parseQuantityValue = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getOriginalUnitPrice = (item) => {
    const raw =
      item?.originalPrice ??
      item?.units?.sale?.price ??
      item?.rate ??
      item?.price ??
      0;
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // "Minimum Sale Quantity" Special Feature (see GlobalSettingsModel.js) -
  // reads the item's own units.sale.minQty; returns null when unset/0/
  // invalid (no minimum) or when the feature is off, so every call site can
  // just check `!= null` without separately re-checking the toggle.
  const minSaleQuantityEnabled = company?.minSaleQuantityEnabled === true;
  const getItemMinSaleQty = (item) => {
    if (!minSaleQuantityEnabled) return null;
    const raw = item?.units?.sale?.minQty ?? item?.minQty;
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
  };
  const showMinSaleQtyWarning = (itemName, minQty, unitLabel) => {
    setMinSaleQtyDialog({ open: true, itemName: itemName || "Item", minQty, unitLabel });
  };

  const getEffectiveUnitPrice = (item) => {
    const override = parsePriceValue(item?.overridePrice);
    if (override != null && override > 0) {
      return override;
    }
    const raw =
      item?.units?.sale?.price ??
      item?.rate ??
      item?.originalPrice ??
      item?.price ??
      0;
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const isItemPriceAdjusted = (item) => {
    const base = getOriginalUnitPrice(item);
    const effective = getEffectiveUnitPrice(item);
    return Math.abs(effective - base) > 0.0001;
  };

// Helper to determine VAT type (inclusive or exclusive)
const isVatInclusive = () => {
  return company?.vatType === "inclusive";
};

// Helper to determine VAT rate (item-based or common)
const getVatRate = (item) => {
  if (company?.vatOptions === "common") {
    return parseFloat(company?.commonVatRate) || 0;
  }
  return parseFloat(item.vat) || 0; // Item-based VAT
};


  // const handleAddToCart = async (item, quantity) => {
  //   const data = await getCurrentStockQty(item._id);
  //   const convertedQty = await getConvertedQuantity({ ...item, quantity }, "sale");
  //   const updatedCartItems = [...cartItems];
  //   const existingItem = updatedCartItems.find((cartItem) => cartItem._id === item._id);
  
  //   if (existingItem) {
  //     const existingConvertedQty = await getConvertedQuantity(existingItem, "sale");
  //     if (existingItem.isStaffReq) {
  //       existingItem.quantity = quantity;
  //       console.log("existing item Updated Quantity", existingItem.quantity);
  //     } else if (existingItem.toggleStock) {
  //       const remainingStock = data.currentStock + existingConvertedQty;
  //       if (remainingStock <= 0) {
  //         alert(`Not enough stock for ${existingItem.itemName}`);
  //         return;
  //       }
  //       if (remainingStock <= existingItem.stockQty.minQty && !minQtyAlerted[existingItem._id]) {
  //         alert(`Minimum Quantity Reached for ${existingItem.itemName}`);
  //         setMinQtyAlerted((prev) => ({
  //           ...prev,
  //           [existingItem._id]: true,
  //         }));
  //       }
  //       existingItem.quantity++;
  //       existingItem.isLowStock = remainingStock <= existingItem.stockQty.minQty;
  //     } else {
  //       existingItem.quantity++;
  //     }
  //   } else {
  //     if (item.isStaffReq) {
  //       updatedCartItems.push({ ...item, quantity });
  //     } else if (item.toggleStock) {
  //       // Check stock before adding new item
  //       if (data.currentStock <= 0) {
  //         alert(`Not enough stock for ${item.itemName}`);
  //         return;
  //       }
  //       const remainingStock = data.currentStock + convertedQty;
  //       const isLowStock = remainingStock <= item.stockQty.minQty;
  //       if (isLowStock && !minQtyAlerted[item._id]) {
  //         alert(`Minimum Quantity Reached for ${item.itemName}`);
  //         setMinQtyAlerted((prev) => ({
  //           ...prev,
  //           [item._id]: true,
  //         }));
  //       }
  //       updatedCartItems.push({ ...item, quantity: item.isStaffReq ? quantity : 1, isLowStock });
  //     } else {
  //       updatedCartItems.push({ ...item, quantity: 1 });
  //     }
  //   }
  //   setCartItems(updatedCartItems);
  //   clearDiscount();
  // };

  // const handleAddToCart = async (item, quantity) => {
  //   const data = await getCurrentStockQty(item._id);
  //   const updatedCartItems = [...cartItems];
  //   const existingItem = updatedCartItems.find((cartItem) => cartItem._id === item._id);
  
  //   if (existingItem) {
  //     // Compute converted quantity for stock check
  //     const existingConvertedQty = await getConvertedQuantity(
  //       { ...existingItem, quantity: existingItem.quantity },
  //       "sale",
  //       item.units?.stock?.unit?._id
  //     );
  //     if (existingItem.isStaffReq) {
  //       existingItem.quantity = quantity; // Set to provided quantity
  //       console.log("existing item Updated Quantity (isStaffReq)", existingItem.quantity);
  //     } else if (existingItem.toggleStock) {
  //       // Check stock with the proposed new quantity
  //       const newQuantity = existingItem.quantity + quantity;
  //       const proposedConvertedQty = await getConvertedQuantity(
  //         { ...existingItem, quantity: newQuantity },
  //         "sale",
  //         item.units?.stock?.unit?._id
  //       );
  //       const remainingStock = data.currentStock + existingConvertedQty - proposedConvertedQty;
  //       // if (remainingStock < 0) {
  //       //   alert(`Not enough stock for ${existingItem.itemName}`);
  //       //   return;
  //       // }
  //       if (remainingStock <= existingItem.stockQty.minQty && !minQtyAlerted[existingItem._id]) {
  //         alert(`Minimum Quantity Reached for ${existingItem.itemName}`);
  //         setMinQtyAlerted((prev) => ({
  //           ...prev,
  //           [existingItem._id]: true,
  //         }));
  //       }
  //       existingItem.quantity = newQuantity; // Accumulate provided quantity
  //       existingItem.isLowStock = remainingStock <= existingItem.stockQty.minQty;
  //       console.log("existing item Updated Quantity (toggleStock)", existingItem.quantity);
  //     } else {
  //       existingItem.quantity += quantity; // Accumulate provided quantity
  //       console.log("existing item Updated Quantity (default)", existingItem.quantity);
  //     }
  //   } else {
  //     // Compute converted quantity for new item
  //     const convertedQty = await getConvertedQuantity(
  //       { ...item, quantity },
  //       "sale",
  //       item.units?.stock?.unit?._id
  //     );
  //     if (item.isStaffReq) {
  //       updatedCartItems.push({ ...item, quantity });
  //       console.log("New item added (isStaffReq)", quantity);
  //     } else if (item.toggleStock) {
  //       const remainingStock = data.currentStock - convertedQty;
  //       // if (remainingStock < 0) {
  //       //   alert(`Not enough stock for ${item.itemName}`);
  //       //   return;
  //       // }
  //       const isLowStock = remainingStock <= item.stockQty.minQty;
  //       if (isLowStock && !minQtyAlerted[item._id]) {
  //         alert(`Minimum Quantity Reached for ${item.itemName}`);
  //         setMinQtyAlerted((prev) => ({
  //           ...prev,
  //           [item._id]: true,
  //         }));
  //       }
  //       updatedCartItems.push({ ...item, quantity, isLowStock });
  //       console.log("New item added (toggleStock)", quantity);
  //     } else {
  //       updatedCartItems.push({ ...item, quantity });
  //       console.log("New item added (default)", quantity);
  //     }
  //   }
  
  //   setCartItems(updatedCartItems);
  //   clearDiscount();
  // };

  const removeFromCart = (index) => {
    const updatedCartItems = [...cartItems];
    const removedItem = updatedCartItems[index];
    updatedCartItems.splice(index, 1);
    if (removedItem.toggleStock) {
      setMinQtyAlerted((prev) => {
        const newState = { ...prev };
        delete newState[removedItem._id];
        return newState;
      });
      setNegativeStockAlerted((prev) => {
        const newState = { ...prev };
        delete newState[removedItem._id];
        return newState;
      });
    }
    setCartItems(updatedCartItems);
    clearDiscount();
  };

  // const incrementQuantity = async (index) => {
  //   const updatedCartItems = [...cartItems];
  //   const item = updatedCartItems[index];
  //   const { quantity, stockQty, itemName, _id, toggleStock } = item;
  //   const currentConvertedQty = await getConvertedQuantity(item, "sale");

  //   if (toggleStock) {
  //     const data = await getCurrentStockQty(_id);
  //     const remainingStock = data.currentStock + currentConvertedQty;
  //     // if (remainingStock <= 0) {
  //     //   alert(`Not enough stock for ${itemName}`);
  //     //   return;
  //     // }
  //     if (remainingStock <= stockQty.minQty && !minQtyAlerted[_id]) {
  //       alert(`Minimum Quantity Reached for ${itemName}`);
  //       setMinQtyAlerted((prev) => ({
  //         ...prev,
  //         [_id]: true,
  //       }));
  //     }
  //     updatedCartItems[index].quantity++;
  //     updatedCartItems[index].isLowStock = remainingStock <= stockQty.minQty;
  //   } else {
  //     updatedCartItems[index].quantity++;
  //   }
  //   setCartItems(updatedCartItems);
  //   clearDiscount();
  // };

  // const decrementQuantity = async (index) => { // Made async
  //   const updatedCartItems = [...cartItems];
  //   if (updatedCartItems[index].quantity > 1) {
  //     const item = updatedCartItems[index];
  //     updatedCartItems[index].quantity--;
  //     if (item.toggleStock) {
  //       const data = await getCurrentStockQty(item._id); // Fetch stock data
  //       const remainingStock = data.currentStock + await getConvertedQuantity(item, "sale");
  //       item.isLowStock = remainingStock <= item.stockQty.minQty;
  //     }
  //     setCartItems(updatedCartItems);
  //     clearDiscount();
  //   }
  // };

  // const handleQuantityChange = async (event, index) => {
  //   const updatedCartItems = [...cartItems];
  //   const inputValue = event.target.value;
  //   const newQuantity = inputValue === "" ? "" : parseInt(inputValue, 10);
  //   const item = updatedCartItems[index];

  //   if (inputValue === "") {
  //     updatedCartItems[index].quantity = "";
  //     setCartItems(updatedCartItems);
  //     return;
  //   }

  //   if (isNaN(newQuantity) || newQuantity < 1) {
  //     alert("Quantity must be at least 1");
  //     updatedCartItems[index].quantity = 1;
  //     setCartItems(updatedCartItems);
  //     return;
  //   }

  //   if (item.toggleStock) {
  //     const data = await getCurrentStockQty(item._id);
  //     const newConvertedQty = await getConvertedQuantity({ ...item, quantity: newQuantity }, "sale");
  //     const remainingStock = data.currentStock + newConvertedQty;
  //     // if (-newConvertedQty > data.currentStock) {
  //     //   alert(`Not enough stock for ${item.itemName}`);
  //     //   updatedCartItems[index].quantity = Math.floor(
  //     //     data.currentStock / -await getConvertedQuantity({ ...item, quantity: 1 }, "sale")
  //     //   );
  //     //   setCartItems(updatedCartItems);
  //     //   return;
  //     // }
  //     if (remainingStock <= item.stockQty.minQty && !minQtyAlerted[item._id]) {
  //       alert(`Minimum Quantity Reached for ${item.itemName}`);
  //       setMinQtyAlerted((prev) => ({
  //         ...prev,
  //         [item._id]: true,
  //       }));
  //     }
  //     updatedCartItems[index].quantity = newQuantity;
  //     updatedCartItems[index].isLowStock = remainingStock <= item.stockQty.minQty;
  //   } else {
  //     updatedCartItems[index].quantity = newQuantity;
  //   }
  //   setCartItems(updatedCartItems);
  //   clearDiscount();
  // };

  const handleOpenQtyDialog = (index) => {
    setSelectedIndex(index);
    setQtyDialogOpen(true);
  };

  const handleCloseQtyDialog = () => {
    setQtyDialogOpen(false);
  };

  // const getSubTotalPrice = () => {
  //   if (
  //     (profileDetail?.vatType === "inclusive" || profile.userProfile.vatType === "inclusive") &&
  //     (!profileDetail || profileDetail.vatType !== "exclusive")
  //   ) {
  //     let subTotal = 0;
  //     for (const item of cartItems) {
  //       subTotal +=
  //         item.quantity *
  //         Math.round(
  //           (parseFloat(item.units?.sale?.price) /
  //             (parseFloat(item.units?.sale?.price) + item.vat)) *
  //             item.units?.sale?.price
  //         );
  //     }
  //     // console.log(subTotal);
  //     return subTotal;
  //   }

  //   let subTotal = 0;
  //   for (const item of cartItems) {
  //     subTotal += item.quantity * item.units?.sale?.price;
  //   }
  //   return subTotal;
  // };

  // const calculateVat = () => {
  //   if (
  //     (profileDetail?.vatType === "inclusive" || profile.userProfile.vatType === "inclusive") &&
  //     (!profileDetail || profileDetail.vatType !== "exclusive")
  //   ) {
  //     let vat = 0;
  //     for (const item of cartItems) {
  //       vat +=
  //         item.quantity *
  //         Math.round(
  //           (item.units?.sale?.price /
  //             (parseFloat(item.units?.sale?.price) + item.vat)) *
  //             item.vat
  //         );
  //     }
  //     return vat;
  //   }

  //   let vat = 0;
  //   if (discountFlag && discountType) {
  //     for (const item of cartItems) {
  //       vat +=
  //         item.quantity *
  //         (parseFloat(item.units?.sale?.price) -
  //           parseFloat(item.units?.sale?.price) * (discountRate / 100)) *
  //         (item.vat / 100);
  //     }
  //   } else if (discountFlag && !discountType) {
  //     const subTotal = getSubTotalPrice();
  //     for (const item of cartItems) {
  //       vat +=
  //         (item.quantity * parseFloat(item.units?.sale?.price) -
  //           (discountVal / subTotal) * (item.quantity * parseFloat(item.units?.sale?.price))) *
  //         (item.vat / 100);
  //     }
  //   } else {
  //     for (const item of cartItems) {
  //       vat += item.quantity * parseFloat(item.units?.sale?.price) * (item.vat / 100);
  //     }
  //   }
  //   return vat;
  // };

  // const getTotalPrice = () => {
  //   const totalPrice =
  //     parseFloat(getSubTotalPrice().toFixed(2)) -
  //     (discountFlag ? parseFloat(discountVal) : 0) +
  //     parseFloat(calculateVat());
  //   return totalPrice;
  // };

  // const calculateChange = () => {
  //   const total = getTotalPrice();
  //   if (receivedAmount >= total) {
  //     return (receivedAmount - total).toFixed(2);
  //   } else {
  //     return "";
  //   }
  // };


  

  // const calculatePrice = (quantity, price) => (quantity * price).toFixed(2);

  //----------------------------------------------------------------------------------------------

  const getSubTotalPrice = () => {
    let subTotal = 0;
    for (const item of cartItems) {
      const price = getEffectiveUnitPrice(item);
      const vatRate = getVatRate(item);
      const quantity = item.quantity || 1;

      if (isVatInclusive()) {
        const priceExclVat = price / (1 + vatRate / 100);
        subTotal += quantity * priceExclVat;
      } else {
        subTotal += quantity * price;
      }
    }
    return parseFloat(subTotal.toFixed(2));
  };

  const getOriginalSubTotalPrice = () => {
    let subTotal = 0;
    for (const item of cartItems) {
      const price = getOriginalUnitPrice(item);
      const vatRate = getVatRate(item);
      const quantity = item.quantity || 1;

      if (isVatInclusive()) {
        const priceExclVat = price / (1 + vatRate / 100);
        subTotal += quantity * priceExclVat;
      } else {
        subTotal += quantity * price;
      }
    }
    return parseFloat(subTotal.toFixed(2));
  };

  const calculateVat = () => {
    let vat = 0;
    for (const item of cartItems) {
      const price = getEffectiveUnitPrice(item);
      const vatRate = getVatRate(item);
      const quantity = item.quantity || 1;

      if (isVatInclusive()) {
        const priceExclVat = price / (1 + vatRate / 100);
        const vatAmount = price - priceExclVat;
        vat += quantity * vatAmount;
      } else {
        let priceAfterDiscount = price;
        if (discountFlag && discountType) {
          priceAfterDiscount = price * (1 - discountRate / 100);
        } else if (discountFlag && !discountType) {
          const subTotal = getSubTotalPrice() || 1;
          const itemTotal = quantity * price;
          priceAfterDiscount =
            price - (discountVal * (itemTotal / subTotal)) / quantity;
        }
        vat += quantity * priceAfterDiscount * (vatRate / 100);
      }
    }
    return parseFloat(vat.toFixed(2));
  };

  const getTotalPrice = () => {
    const subTotal = getSubTotalPrice();
    const vat = calculateVat();
    const totalPrice = subTotal - (discountFlag ? parseFloat(discountVal) : 0) + vat;
    const { rounded } = applyCurrencyRounding(totalPrice, company?.currency, company?.roundingRules);
    return rounded;
  };

  // The cash-rounding delta already folded into getTotalPrice() above -
  // exposed separately so it can be persisted on the invoice (its own field,
  // not silently absorbed into `total`) and shown as its own receipt line.
  const getRoundOff = () => {
    const subTotal = getSubTotalPrice();
    const vat = calculateVat();
    const totalPrice = subTotal - (discountFlag ? parseFloat(discountVal) : 0) + vat;
    const { roundOff } = applyCurrencyRounding(totalPrice, company?.currency, company?.roundingRules);
    return roundOff;
  };

  const calculateChange = () => {
    const total = getTotalPrice();
    if (receivedAmount >= total) {
      return (receivedAmount - total).toFixed(2);
    } else {
      return "";
    }
  };

  const calculatePrice = (item) =>
    ((item?.quantity || 0) * getEffectiveUnitPrice(item)).toFixed(2);

//------------------------------------------------------------------
// Evaluates both the minimum-stock warning and the negative-stock notice for
// a single stock-tracked cart item, given its resulting stock after the
// pending change. The two are mutually exclusive: once stock has actually
// gone negative, the plain "Minimum stock reached" message no longer applies
// (per product requirement) - only the dedicated Negative Stock indicator
// does, and it carries its own debounce (negativeStockAlerted) so it fires
// once per dip, not on every subsequent increment.
const evaluateStockAlerts = (item, remainingStock) => {
  const minQty = parseFloat(item?.stockQty?.minQty || 0);
  const unitLabel = item?.units?.stock?.unit?.abbreviation || "";
  const itemName = item?.itemName || "Item";
  const isNegativeStock = remainingStock < 0;
  const isLowStock = !isNegativeStock && remainingStock <= minQty;

  if (isNegativeStock) {
    if (!negativeStockAlerted[item._id]) {
      setNegativeStockDialog({ open: true, itemName, remainingStock, unitLabel });
      setNegativeStockAlerted((prev) => ({ ...prev, [item._id]: true }));
    }
  } else if (negativeStockAlerted[item._id]) {
    setNegativeStockAlerted((prev) => {
      const newState = { ...prev };
      delete newState[item._id];
      return newState;
    });
  }

  if (isLowStock) {
    if (!minQtyAlerted[item._id]) {
      setLowStockDialog({ open: true, itemName, remainingStock, minQty, unitLabel });
      setMinQtyAlerted((prev) => ({ ...prev, [item._id]: true }));
    }
  } else if (minQtyAlerted[item._id]) {
    // Reset alert if stock goes above minimum (or dips into negative, which
    // is reported separately above instead).
    setMinQtyAlerted((prev) => {
      const newState = { ...prev };
      delete newState[item._id];
      return newState;
    });
  }

  return { isLowStock, isNegativeStock };
};

// const handleAddToCart = async (item, quantity) => {
//   const data = await getCurrentStockQty(item._id);
//   const updatedCartItems = [...cartItems];
//   const existingItem = updatedCartItems.find((cartItem) => cartItem._id === item._id);

//   if (existingItem) {
//     // Compute current converted quantity in cart
//     const existingConvertedQty = await getConvertedQuantity(
//       { ...existingItem, quantity: existingItem.quantity },
//       "sale",
//       item.units?.stock?.unit?._id
//     );
//     if (existingItem.isStaffReq) {
//       existingItem.quantity = quantity;
//       console.log("existing item Updated Quantity (isStaffReq)", existingItem.quantity);
//     } else if (existingItem.toggleStock) {
//       // Compute proposed new quantity and converted quantity
//       const newQuantity = existingItem.quantity + quantity;
//       const proposedConvertedQty = await getConvertedQuantity(
//         { ...existingItem, quantity: newQuantity },
//         "sale",
//         item.units?.stock?.unit?._id
//       );
//       const remainingStock = data.currentStock - proposedConvertedQty;

//       // if (remainingStock < 0) {
//       //   alert(`Not enough stock for ${existingItem.itemName}`);
//       //   return;
//       // }

//       // Handle minimum stock alert
//       existingItem.isLowStock = handleMinStockAlert(
//         existingItem,
//         remainingStock,
//         minQtyAlerted,
//         setMinQtyAlerted
//       );

//       existingItem.quantity = newQuantity;
//       console.log("existing item Updated Quantity (toggleStock)", existingItem.quantity);
//     } else {
//       existingItem.quantity += quantity;
//       console.log("existing item Updated Quantity (default)", existingItem.quantity);
//     }
//   } else {
//     // Compute converted quantity for new item
//     const convertedQty = await getConvertedQuantity(
//       { ...item, quantity },
//       "sale",
//       item.units?.stock?.unit?._id
//     );
//     if (item.isStaffReq) {
//       updatedCartItems.push({ ...item, quantity });
//       console.log("New item added (isStaffReq)", quantity);
//     } else if (item.toggleStock) {
//       const remainingStock = data.currentStock - convertedQty;
//       // if (remainingStock < 0) {
//       //   alert(`Not enough stock for ${item.itemName}`);
//       //   return;
//       // }

//       // Handle minimum stock alert
//       const isLowStock = handleMinStockAlert(item, remainingStock, minQtyAlerted, setMinQtyAlerted);

//       updatedCartItems.push({ ...item, quantity, isLowStock });
//       console.log("New item added (toggleStock)", quantity);
//     } else {
//       updatedCartItems.push({ ...item, quantity });
//       console.log("New item added (default)", quantity);
//     }
//   }

//   setCartItems(updatedCartItems);
//   clearDiscount();
// };

const addToCartNow = async (item, quantity) => {
  if (!item || !item._id) {
    console.error("Invalid item or missing _id:", item);
    return;
  }
  let normalizedQuantity = parseQuantityValue(quantity);
  if (normalizedQuantity == null || normalizedQuantity <= 0) {
    console.error("Invalid quantity:", quantity);
    return;
  }

  const updatedCartItems = [...cartItems];
  const existingItem = updatedCartItems.find((cartItem) => cartItem._id === item._id);

  // "Minimum Sale Quantity" Special Feature (see GlobalSettingsModel.js) -
  // only bumps quantity the FIRST time an item enters the cart; adding more
  // onto an item already in the cart (which, by having gotten in, already
  // satisfies the minimum) just adds whatever quantity was actually
  // requested - reassigning normalizedQuantity here means every downstream
  // use below (stock check, session add, local cart push) picks it up
  // automatically, without touching each of those sites individually.
  if (!existingItem) {
    const minSaleQty = getItemMinSaleQty(item);
    if (minSaleQty != null && normalizedQuantity < minSaleQty) {
      normalizedQuantity = minSaleQty;
      showMinSaleQtyWarning(item.itemName, minSaleQty, item.units?.sale?.unit?.abbreviation || "");
    }
  }
  const basePrice =
    parsePriceValue(item.originalPrice) ??
    parsePriceValue(item.units?.sale?.price) ??
    parsePriceValue(item.rate) ??
    0;
  const overrideIncoming = parsePriceValue(item.overridePrice);
  const effectiveIncoming =
    overrideIncoming != null && overrideIncoming > 0
      ? overrideIncoming
      : parsePriceValue(item.units?.sale?.price) ??
        parsePriceValue(item.rate) ??
        basePrice;

  // Stock check runs once, up front, before any session/cart mutation - so a
  // blocked add doesn't partially update the hold-bill session while leaving
  // the local cart untouched (or vice versa).
  const isStaffReq = existingItem ? existingItem.isStaffReq : item.isStaffReq;
  const tracksStock = existingItem ? existingItem.toggleStock : item.toggleStock;
  let remainingStockForAlert = null;

  if (!isStaffReq && tracksStock) {
    const data = await getCurrentStockQty(item._id);
    const currentCartQty = existingItem ? (parseQuantityValue(existingItem.quantity) || 0) : 0;
    const proposedConvertedQty = await getConvertedQuantity(
      { ...(existingItem || item), quantity: currentCartQty + normalizedQuantity },
      "sale",
      item.units?.stock?.unit?._id
    );
    remainingStockForAlert = remainingAfterProposedSale(
      data.currentStock,
      item._id,
      proposedConvertedQty
    );

    if (!allowNegativeStock && remainingStockForAlert < 0) {
      setOutOfStockDialog({
        open: true,
        itemName: item.itemName || existingItem?.itemName || "Item",
        availableStock: availableIncludingThisInvoice(data.currentStock, item._id),
        unitLabel: item.units?.stock?.unit?.abbreviation || "",
      });
      return;
    }
  }

  if (orderSession?.activeSessionId) {
    try {
      const sessionItems = orderSession.activeSession?.items || [];
      const normalizeId = (val) => (val == null ? "" : String(val));
      const sameRemark = (a, b) => (a ?? "") === (b ?? "");
      const almostEqual = (a, b) => Math.abs((parseFloat(a) || 0) - (parseFloat(b) || 0)) < 0.0001;

      const matchIndex = sessionItems.findIndex((si) => {
        const siItemId = si.itemId?._id || si.itemId;
        if (normalizeId(siItemId) !== normalizeId(item._id)) return false;
        if (si.kotPrinted) return false;
        if (!sameRemark(si.remark, item.remark)) return false;
        const siRate = parseFloat(si.rate ?? 0);
        const siOriginal = parseFloat(si.originalRate ?? si.rate ?? 0);
        if (!almostEqual(siRate, effectiveIncoming)) return false;
        if (!almostEqual(siOriginal, basePrice)) return false;
        return true;
      });

      if (matchIndex >= 0) {
        const current = sessionItems[matchIndex];
        const nextQty = (parseQuantityValue(current.qty) || 0) + normalizedQuantity;
        await orderSession.updateItemInSession(orderSession.activeSessionId, {
          index: matchIndex,
          qty: nextQty,
        });
      } else {
        await orderSession.addItemToSession(orderSession.activeSessionId, {
          ...item,
          quantity: normalizedQuantity,
        });
      }
    } catch (err) {
      console.error("Failed to add to session:", err);
      return;
    }
  }

  if (existingItem) {
    if (existingItem.originalPrice == null || Number.isNaN(existingItem.originalPrice)) {
      existingItem.originalPrice = basePrice;
    }
    if (existingItem.isStaffReq) {
      existingItem.quantity = normalizedQuantity;
    } else if (existingItem.toggleStock) {
      // Stock availability was already checked above (remainingStockForAlert);
      // just apply the stock alerts and the new quantity here.
      const { isLowStock, isNegativeStock } = evaluateStockAlerts(existingItem, remainingStockForAlert);
      existingItem.isLowStock = isLowStock;
      existingItem.isNegativeStock = isNegativeStock;
      existingItem.stockAfterSale = remainingStockForAlert;
      existingItem.quantity = (parseQuantityValue(existingItem.quantity) || 0) + normalizedQuantity;
    } else {
      // No stock checks for toggleStock: false
      existingItem.quantity = (parseQuantityValue(existingItem.quantity) || 0) + normalizedQuantity;
    }
    if (overrideIncoming != null && overrideIncoming > 0) {
      existingItem.overridePrice = overrideIncoming;
    }
    existingItem.priceAdjusted = isItemPriceAdjusted(existingItem);
  } else {
    if (item.isStaffReq) {
      updatedCartItems.push({
        ...item,
        quantity: normalizedQuantity,
        originalPrice: basePrice,
        overridePrice: overrideIncoming,
        priceAdjusted:
          overrideIncoming != null && Math.abs(overrideIncoming - basePrice) > 0.0001,
      });
    } else if (item.toggleStock) {
      // Stock availability was already checked above (remainingStockForAlert).
      const { isLowStock, isNegativeStock } = evaluateStockAlerts(item, remainingStockForAlert);
      updatedCartItems.push({
        ...item,
        quantity: normalizedQuantity,
        isLowStock,
        isNegativeStock,
        stockAfterSale: remainingStockForAlert,
        originalPrice: basePrice,
        overridePrice: overrideIncoming,
        priceAdjusted:
          overrideIncoming != null && Math.abs(overrideIncoming - basePrice) > 0.0001,
      });
    } else {
      // No stock checks for toggleStock: false
      updatedCartItems.push({
        ...item,
        quantity: normalizedQuantity,
        originalPrice: basePrice,
        overridePrice: overrideIncoming,
        priceAdjusted:
          overrideIncoming != null && Math.abs(overrideIncoming - basePrice) > 0.0001,
      });
    }
  }

  setCartItems(updatedCartItems);
  clearDiscount();
};

// Rapid taps on the same card (the search now stays put after adding, so
// the card doesn't move away) used to race: each add awaits the stock /
// session calls against the cartItems of the render it started from, so a
// second tap landing during that wait overwrote the first one's result and
// the quantity came out short. Adds are queued so each one starts only after
// the previous finished, and always runs the latest addToCartNow (fresh
// cartItems / session state from the re-render the previous add triggered).
const addToCartNowRef = useRef(addToCartNow);
addToCartNowRef.current = addToCartNow;
const addToCartQueueRef = useRef(Promise.resolve());
const handleAddToCart = (item, quantity) => {
  const run = addToCartQueueRef.current.then(() =>
    addToCartNowRef.current(item, quantity)
  );
  addToCartQueueRef.current = run.catch((err) => {
    console.error("Add to cart failed:", err);
  });
  return run;
};

const incrementQuantity = async (index) => {
  const updatedCartItems = [...cartItems];
  const item = updatedCartItems[index];
  const { _id, toggleStock } = item;
  const currentQty = parseQuantityValue(item.quantity) || 0;

  if (toggleStock) {
    const data = await getCurrentStockQty(_id);
    const proposedConvertedQty = await getConvertedQuantity(
      { ...item, quantity: currentQty + 1 },
      "sale",
      item.units?.stock?.unit?._id
    );
    const remainingStock = remainingAfterProposedSale(
      data.currentStock,
      _id,
      proposedConvertedQty
    );

    if (!allowNegativeStock && remainingStock < 0) {
      setOutOfStockDialog({
        open: true,
        itemName: item.itemName || "Item",
        availableStock: availableIncludingThisInvoice(data.currentStock, _id),
        unitLabel: item.units?.stock?.unit?.abbreviation || "",
      });
      return;
    }

    // Handle stock alerts (minimum-stock or negative-stock, mutually exclusive)
    const { isLowStock, isNegativeStock } = evaluateStockAlerts(item, remainingStock);
    updatedCartItems[index].isLowStock = isLowStock;
    updatedCartItems[index].isNegativeStock = isNegativeStock;
    updatedCartItems[index].stockAfterSale = remainingStock;

    updatedCartItems[index].quantity = currentQty + 1;
  } else {
    updatedCartItems[index].quantity = currentQty + 1;
  }
  setCartItems(updatedCartItems);
  clearDiscount();
};

const decrementQuantity = async (index) => {
  const updatedCartItems = [...cartItems];
  const item = updatedCartItems[index];
  const currentQty = parseQuantityValue(item.quantity) || 0;
  // "Minimum Sale Quantity" Special Feature - floor becomes the item's own
  // minimum (when it has one) instead of the usual hard floor of 1.
  const minSaleQty = getItemMinSaleQty(item);
  const floor = minSaleQty != null ? minSaleQty : 1;

  if (currentQty > floor) {
    if (item.toggleStock) {
      const data = await getCurrentStockQty(item._id);
      const newConvertedQty = await getConvertedQuantity(
        { ...item, quantity: currentQty - 1 },
        "sale",
        item.units?.stock?.unit?._id
      );
      const remainingStock = remainingAfterProposedSale(
        data.currentStock,
        item._id,
        newConvertedQty
      );

      // Handle stock alerts (minimum-stock or negative-stock, mutually exclusive)
      const { isLowStock, isNegativeStock } = evaluateStockAlerts(item, remainingStock);
      updatedCartItems[index].isLowStock = isLowStock;
      updatedCartItems[index].isNegativeStock = isNegativeStock;
      updatedCartItems[index].stockAfterSale = remainingStock;
    }
    updatedCartItems[index].quantity = currentQty - 1;
    setCartItems(updatedCartItems);
    clearDiscount();
  } else if (minSaleQty != null && currentQty <= minSaleQty) {
    showMinSaleQtyWarning(item.itemName, minSaleQty, item.units?.sale?.unit?.abbreviation || "");
  }
};

const handleQuantityChange = async (event, index) => {
  const updatedCartItems = [...cartItems];
  const inputValue = event.target.value;
  const newQuantity = inputValue === "" ? "" : parseFloat(inputValue);
  const item = updatedCartItems[index];

  if (inputValue === "") {
    updatedCartItems[index].quantity = "";
    setCartItems(updatedCartItems);
    return;
  }

  if (isNaN(newQuantity)) {
    return;
  }

  // "Minimum Sale Quantity" Special Feature - same "reject, leave the
  // previous value in place" pattern as the out-of-stock block below,
  // rather than snapping the field to the minimum mid-keystroke (which
  // would fight a cashier actively typing a larger number).
  const minSaleQty = getItemMinSaleQty(item);
  if (minSaleQty != null && newQuantity < minSaleQty) {
    showMinSaleQtyWarning(item.itemName, minSaleQty, item.units?.sale?.unit?.abbreviation || "");
    return;
  }

  if (item.toggleStock) {
    const data = await getCurrentStockQty(item._id);
    const newConvertedQty = await getConvertedQuantity(
      { ...item, quantity: newQuantity },
      "sale",
      item.units?.stock?.unit?._id
    );
    const remainingStock = remainingAfterProposedSale(
      data.currentStock,
      item._id,
      newConvertedQty
    );

    if (!allowNegativeStock && remainingStock < 0) {
      setOutOfStockDialog({
        open: true,
        itemName: item.itemName || "Item",
        availableStock: availableIncludingThisInvoice(data.currentStock, item._id),
        unitLabel: item.units?.stock?.unit?.abbreviation || "",
      });
      return;
    }

    // Handle stock alerts (minimum-stock or negative-stock, mutually exclusive)
    const { isLowStock, isNegativeStock } = evaluateStockAlerts(item, remainingStock);
    updatedCartItems[index].isLowStock = isLowStock;
    updatedCartItems[index].isNegativeStock = isNegativeStock;
    updatedCartItems[index].stockAfterSale = remainingStock;

    updatedCartItems[index].quantity = newQuantity;
  } else {
    updatedCartItems[index].quantity = newQuantity;
  }
  setCartItems(updatedCartItems);
  clearDiscount();
};

const updateItemPrice = async (index, value) => {
  const updatedCartItems = [...cartItems];
  if (index < 0 || index >= updatedCartItems.length) return;
  const item = { ...updatedCartItems[index] };

  if (item.originalPrice == null || Number.isNaN(item.originalPrice)) {
    item.originalPrice = getOriginalUnitPrice(item);
  }

  const parsed = parsePriceValue(value);
  if (parsed != null && parsed > 0) {
    item.overridePrice = parsed;
  } else {
    item.overridePrice = null;
  }
  item.priceAdjusted = isItemPriceAdjusted(item);

  updatedCartItems[index] = item;
  setCartItems(updatedCartItems);
  clearDiscount();

  if (orderSession?.activeSessionId && item?._id) {
    try {
      await orderSession.updateItemInSession(orderSession.activeSessionId, {
        itemId: item._id,
        rate: getEffectiveUnitPrice(item),
        originalRate: getOriginalUnitPrice(item),
        priceAdjusted: item.priceAdjusted,
      });
      if (typeof orderSession.refreshActiveSession === "function") {
        await orderSession.refreshActiveSession();
      }
    } catch (error) {
      console.error("Failed to update session item price:", error);
    }
  }
};

  return (
    <>
      <CartContext.Provider
        value={{
          cartItems,
          QtydialogOpen,
          selectedIndex,
          receivedAmount,
          handleAddToCart,
          removeFromCart,
          incrementQuantity,
          decrementQuantity,
          calculatePrice,
          getTotalPrice,
          getRoundOff,
          getSubTotalPrice,
          getOriginalSubTotalPrice,
          getEffectiveUnitPrice,
          getOriginalUnitPrice,
          isItemPriceAdjusted,
          handleOpenQtyDialog,
          handleCloseQtyDialog,
          handleQuantityChange,
          updateItemPrice,
          setReceivedAmount,
          calculateChange,
          calculateVat,
          setCartItems,
          isCartSubmitting,
          setIsCartSubmitting,
          vat,
          setVat,
          hydrateInvoiceCart,
          clearInvoiceStockCredit,
          remainingAfterProposedSale,
          availableIncludingThisInvoice,
          getItemMinSaleQty,
          showMinSaleQtyWarning,
        }}
      >
        {children}
      </CartContext.Provider>
      <Dialog
        open={lowStockDialog.open}
        onClose={() => setLowStockDialog((prev) => ({ ...prev, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <WarningRoundedIcon style={{ color: "#ef7f1a", fontSize: 28 }} />
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Minimum stock reached
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 12 }}>
            {lowStockDialog.itemName} is now below the minimum level.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Current stock: {Number(lowStockDialog.remainingStock || 0).toFixed(2)}{" "}
            {lowStockDialog.unitLabel}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Minimum stock: {Number(lowStockDialog.minQty || 0).toFixed(2)}{" "}
            {lowStockDialog.unitLabel}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setLowStockDialog((prev) => ({ ...prev, open: false }))}
            variant="contained"
            style={{ backgroundColor: "#ef7f1a", color: "white" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={outOfStockDialog.open}
        onClose={() => setOutOfStockDialog((prev) => ({ ...prev, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <WarningRoundedIcon style={{ color: "#dc2626", fontSize: 28 }} />
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Not enough stock
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 12 }}>
            {outOfStockDialog.itemName} cannot be sold at this quantity - stock would go to zero or below.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Available stock: {Number(outOfStockDialog.availableStock || 0).toFixed(2)}{" "}
            {outOfStockDialog.unitLabel}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setOutOfStockDialog((prev) => ({ ...prev, open: false }))}
            variant="contained"
            style={{ backgroundColor: "#dc2626", color: "white" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={minSaleQtyDialog.open}
        onClose={() => setMinSaleQtyDialog((prev) => ({ ...prev, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <WarningRoundedIcon style={{ color: "#c2410c", fontSize: 28 }} />
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Minimum sale quantity
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 12 }}>
            {minSaleQtyDialog.itemName} can only be sold in quantities of{" "}
            {Number(minSaleQtyDialog.minQty || 0).toFixed(
              Number.isInteger(minSaleQtyDialog.minQty) ? 0 : 2
            )}{" "}
            {minSaleQtyDialog.unitLabel} or more.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setMinSaleQtyDialog((prev) => ({ ...prev, open: false }))}
            variant="contained"
            style={{ backgroundColor: "#c2410c", color: "white" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={negativeStockDialog.open}
        onClose={() => setNegativeStockDialog((prev) => ({ ...prev, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <WarningRoundedIcon style={{ color: "#b91c1c", fontSize: 28 }} />
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Negative Stock
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 12 }}>
            {negativeStockDialog.itemName} has been sold below zero stock. This sale is allowed
            because "Allow Negative Stock" is turned on in Stock Settings.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Stock: {Number(negativeStockDialog.remainingStock || 0).toFixed(2)}{" "}
            {negativeStockDialog.unitLabel}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setNegativeStockDialog((prev) => ({ ...prev, open: false }))}
            variant="contained"
            style={{ backgroundColor: "#b91c1c", color: "white" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
